import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  StreamFrame,
  VerbaiqCost,
  VerbaiqDataTable,
  VerbaiqMessage,
  VerbaiqRunState,
  VerbaiqTraceItem,
} from "../../pages/VfCopilot/types/vfCopilot.types";

export interface VerbaiqState {
  messages: VerbaiqMessage[];
  runState: VerbaiqRunState;
  answer: string;
  trace: VerbaiqTraceItem[];
  dataTables: VerbaiqDataTable[];
  followUps: string[];
  cost: VerbaiqCost;
  cursor: number;
  errorMessage: string | null;
  // The machine-readable code carried alongside errorMessage (frame.data.error_code), when the
  // producer set one, so a consumer can branch on it instead of just showing plain text. null
  // for error frames that carry no code (e.g. a generic transcript-load failure dispatched locally).
  errorCode: string | null;
  // Increments on every terminal resolution (done/empty/truncated/error) so a consumer can
  // detect "a run just finished" even when two runs resolve to the same runState string.
  terminalSeq: number;
  // The server-assigned seq the in-flight run's answer will land at (start_run's seq + 1) — used
  // to commit the turn at its true seq instead of a locally-counted index that can drift from
  // what actually got persisted (system-free today, but the two are computed independently).
  pendingAnswerSeq: number | null;
  // Epoch ms the current run actually started at (not when this component/tab last observed
  // it) — lets a live elapsed-time timer survive a session-switch-and-back without resetting.
  runStartedAt: number | null;
}

const TRACE_KINDS = ["thinking", "step", "tool_activity", "plan", "diagnostic"];
const NOTE_KINDS = ["thinking", "plan", "diagnostic"];
const LABEL_BY_KIND: Record<string, string> = {
  thinking: "Thinking",
  plan: "Plan",
  diagnostic: "Note",
};
const emptyCost: VerbaiqCost = { cost_usd: 0, llm_calls: 0, cached_pct: 0 };

// Shown when a run completes normally but produced no content — shared so analytics can tell
// this "empty" outcome apart from a genuine stream error.
export const NO_RESPONSE_MESSAGE =
  "VerbaIQ finished without returning a response. Please try again.";

const asString = (value: unknown, fallback = ""): string =>
  typeof value === "string" ? value : fallback;

const asNumber = (value: unknown, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const clearLiveRun = (state: VerbaiqState): void => {
  state.runState = "idle";
  state.answer = "";
  state.trace = [];
  state.dataTables = [];
  state.followUps = [];
  state.cost = { ...emptyCost };
  state.cursor = 0;
  state.errorMessage = null;
  state.errorCode = null;
  state.pendingAnswerSeq = null;
  state.runStartedAt = null;
};

const initialState: VerbaiqState = {
  messages: [],
  runState: "idle",
  answer: "",
  trace: [],
  dataTables: [],
  followUps: [],
  cost: { ...emptyCost },
  cursor: 0,
  errorMessage: null,
  errorCode: null,
  terminalSeq: 0,
  pendingAnswerSeq: null,
  runStartedAt: null,
};

const buildTraceItem = (event: string, data: Record<string, unknown>): VerbaiqTraceItem => {
  const kind = (TRACE_KINDS.includes(event) ? event : "diagnostic") as VerbaiqTraceItem["kind"];
  const planSteps = Array.isArray(data.steps)
    ? (data.steps as Record<string, unknown>[]).map((raw) => ({
        step: asString(raw?.step),
        status: asString(raw?.status, "pending"),
      }))
    : undefined;
  return {
    key: asString(data.key, kind),
    kind,
    label: LABEL_BY_KIND[kind] ?? asString(data.label, "Analysis"),
    status: NOTE_KINDS.includes(kind) ? "done" : asString(data.status, "running"),
    detail:
      asString(data.result_hint) ||
      asString(data.task) ||
      asString(data.message) ||
      asString(data.text),
    steps: planSteps,
  };
};

const buildDataTable = (data: Record<string, unknown>, index: number): VerbaiqDataTable => ({
  key: `table:${index}`,
  title: asString(data.title, "Results"),
  columns: Array.isArray(data.columns) ? (data.columns as string[]) : [],
  rows: Array.isArray(data.rows) ? (data.rows as unknown[][]) : [],
  totalCount: typeof data.total_count === "number" ? data.total_count : undefined,
  downloadFilename:
    typeof data.download_filename === "string" ? data.download_filename : undefined,
});

export const traceItemsFromEvents = (
  events: { type: string; data: Record<string, unknown> }[]
): VerbaiqTraceItem[] => events.map((event) => buildTraceItem(event.type, event.data ?? {}));

export const dataTablesFromPayloads = (
  payloads: Record<string, unknown>[]
): VerbaiqDataTable[] => payloads.map((payload, index) => buildDataTable(payload ?? {}, index));

const upsertTrace = (state: VerbaiqState, item: VerbaiqTraceItem): void => {
  const existing = state.trace.findIndex((entry) => entry.key === item.key);
  if (existing === -1) state.trace.push(item);
  else state.trace[existing] = { ...state.trace[existing], ...item };
};

const commitLiveTurn = (
  state: VerbaiqState,
  truncated: boolean,
  executionMs?: number,
  status?: VerbaiqMessage["status"]
): void => {
  state.terminalSeq += 1;
  const hasContent =
    Boolean(state.answer) || state.trace.length > 0 || state.dataTables.length > 0;
  // Push unconditionally, even with no content — vf-core's relay_service commits an assistant
  // message on every seal regardless of content, so messages.length must track its count 1:1 or
  // later local seqs (userTurnAdded/userTurnEdited's array-length math) drift from the backend's.
  state.messages.push({
    role: "assistant",
    content: state.answer,
    // pendingAnswerSeq is the seq start_run's response promised this answer — falls back to
    // the local count only for a run reattached without it (reload mid-stream; that view gets
    // corrected from the server's own seq once the run settles regardless, see useVerbaiq.ts).
    seq: state.pendingAnswerSeq ?? state.messages.length,
    trace: state.trace,
    dataTables: state.dataTables,
    followUps: state.followUps,
    executionMs,
    createdAt: Date.now(),
    status,
  });
  state.pendingAnswerSeq = null;
  state.answer = "";
  state.trace = [];
  state.dataTables = [];
  state.followUps = [];
  // A run that completes normally but produced nothing would otherwise leave the user
  // staring at their question with no answer and no error — surface it instead. Truncated runs
  // (stopped or cut off) are exempt: a stop with no content isn't an error, it's just a stop.
  if (!hasContent && !truncated) {
    state.runState = "error";
    state.errorMessage = NO_RESPONSE_MESSAGE;
    return;
  }
  state.runState = truncated ? "truncated" : "done";
};

const foldFrame = (state: VerbaiqState, frame: StreamFrame): void => {
  const data = frame.data ?? {};
  // Drop already-seen frames — a resumed stream must not double-append chunks/tables.
  if (frame.id !== null && frame.id <= state.cursor) return;
  if (frame.id !== null) state.cursor = frame.id;
  switch (frame.event) {
    case "chunk":
      state.answer += asString(data.text);
      return;
    case "thinking": {
      const key = asString(data.key, "thinking");
      const incoming = asString(data.text);
      const existing = state.trace.find((entry) => entry.key === key);
      const detail = data.append && existing ? (existing.detail ?? "") + incoming : incoming;
      upsertTrace(state, { key, kind: "thinking", label: "Thinking", status: "done", detail });
      return;
    }
    case "step":
    case "tool_activity":
    case "plan":
    case "diagnostic":
      upsertTrace(state, buildTraceItem(frame.event, data));
      return;
    case "data_table":
      state.dataTables.push(buildDataTable(data, state.dataTables.length));
      return;
    case "follow_ups":
      state.followUps = Array.isArray(data.suggestions) ? (data.suggestions as string[]) : [];
      return;
    case "clarification": {
      // copilot is asking the user to choose — show the question inline and render its options as
      // the same quick-reply chips as follow-ups, so clicking one sends the chosen answer.
      const question = asString(data.question);
      if (question) state.answer += (state.answer ? "\n\n" : "") + question;
      state.followUps = Array.isArray(data.options) ? (data.options as string[]) : [];
      return;
    }
    case "cost":
      state.cost = {
        cost_usd: asNumber(data.cost_usd, state.cost.cost_usd),
        llm_calls: asNumber(data.llm_calls, state.cost.llm_calls),
        cached_pct: asNumber(data.cached_pct, state.cost.cached_pct),
      };
      return;
    case "control":
      if (asString(data.state) === "paused") state.runState = "paused";
      else if (state.runState === "paused") state.runState = "running";
      return;
    case "done":
      commitLiveTurn(
        state,
        asString(data.status) === "truncated",
        asNumber(data.execution_time_ms, 0) || undefined
      );
      return;
    case "error": {
      const errorMessage = asString(data.message, "Something went wrong. Please try again.");
      const errorCode = typeof data.error_code === "string" ? data.error_code : null;
      // vf-core's relay only commits an assistant message on an error run if content had
      // already streamed before the failure (a truncated commit, mirroring the stop path) —
      // with no content it sets the run to ERROR without inserting a message at all. Mirror
      // that exactly here, or the "has content" branch under-counts messages.length by one
      // against the backend's persisted count, and the next edit-resend's edit_from_seq
      // (computed from local seq) would delete/target the wrong turn.
      const hadContent =
        Boolean(state.answer) || state.trace.length > 0 || state.dataTables.length > 0;
      if (hadContent) {
        commitLiveTurn(state, true);
        state.runState = "error";
        state.errorMessage = errorMessage;
        state.errorCode = errorCode;
        return;
      }
      state.runState = "error";
      state.errorMessage = errorMessage;
      state.errorCode = errorCode;
      state.terminalSeq += 1;
      return;
    }
    default:
      return;
  }
};

const verbaIQSlice = createSlice({
  name: "verbaIQ",
  initialState,
  reducers: {
    sessionLoaded: (state, action: PayloadAction<VerbaiqMessage[]>) => {
      clearLiveRun(state);
      state.messages = action.payload;
    },
    userTurnAdded: (
      state,
      action: PayloadAction<
        | {
            content: string;
            askMention?: string;
            askMentionKind?: string;
            askMentionNames?: string[];
          }
        | string
      >
    ) => {
      // Accepts a bare string for the plain-chat callers; an attached ask passes the mention too so
      // the live bubble marks it apart from the question, exactly as a reloaded one does.
      const payload =
        typeof action.payload === "string" ? { content: action.payload } : action.payload;
      state.messages.push({
        role: "user",
        content: payload.content,
        askMention: payload.askMention,
        askMentionKind: payload.askMentionKind,
        askMentionNames: payload.askMentionNames,
        seq: state.messages.length,
        createdAt: Date.now(),
      });
    },
    // Editing an earlier user turn discards every turn after it (mirroring the backend's
    // delete-at-seq-and-resend) and re-adds the edited text as a fresh turn at that same seq.
    userTurnEdited: (state, action: PayloadAction<{ seq: number; message: string }>) => {
      state.messages = state.messages.slice(0, action.payload.seq);
      state.messages.push({
        role: "user",
        content: action.payload.message,
        seq: state.messages.length,
        createdAt: Date.now(),
      });
    },
    runStarted: (
      state,
      action: PayloadAction<{ fromCursor?: number; answerSeq?: number; startedAt?: number }>
    ) => {
      state.runState = "running";
      state.answer = "";
      state.trace = [];
      state.dataTables = [];
      state.followUps = [];
      state.cost = { ...emptyCost };
      state.cursor = action.payload.fromCursor ?? 0;
      state.errorMessage = null;
      state.errorCode = null;
      state.pendingAnswerSeq = action.payload.answerSeq ?? null;
      state.runStartedAt = action.payload.startedAt ?? Date.now();
    },
    frameReceived: (state, action: PayloadAction<StreamFrame>) => {
      foldFrame(state, action.payload);
    },
    // Stop keeps whatever streamed so far as a truncated turn instead of discarding it, and marks
    // it `status: "stopped"` unconditionally — MessageRow renders a "Stopped" indicator off that
    // field rather than relying on content the run may never have gotten around to producing.
    runStopped: (state) => {
      commitLiveTurn(state, true, undefined, "stopped");
    },
    verbaiqReset: () => {
      return { ...initialState, cost: { ...emptyCost } };
    },
  },
});

export const {
  sessionLoaded,
  userTurnAdded,
  userTurnEdited,
  runStarted,
  frameReceived,
  runStopped,
  verbaiqReset,
} = verbaIQSlice.actions;
export default verbaIQSlice;
